import React, { useState } from "react";
import useTranslator from "../../../hooks/useTranslator";
import { getCurrentDate } from "../../../helpers/Utils";
import DateRangeFilter from "../../../components/shared/fields/DateRangeFilter";
import DashboardSection from "../../dashboard/components/DashboardSection";
import "../../dashboard/components/dashboard-sections.css";
import { CycleIconSvg } from "../../../svgFiles/CycleIconSvg";
import { PackageIconSvg } from "../../../svgFiles/PackageIconSvg";
import { UsersIconSvg } from "../../../svgFiles/UsersIconSvg";

const BrokerageCard = ({ title, icon, children }) => (
  <div className="cmn_card" style={{ overflow: "hidden" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", background: "#fff", borderBottom: "1px solid #f0f1f3" }}>
      <span style={{ fontFamily: "'Public Sans', serif", fontWeight: 600, fontSize: 15, color: "#1d1f2c", display: "flex", alignItems: "center", gap: 6 }}>
        {icon && <span style={{ color: "#667085", display: "flex", alignItems: "center" }}>{icon}</span>}
        {title}
      </span>
    </div>
    <div style={{ background: "#fff", padding: "14px 16px" }}>
      {children}
    </div>
  </div>
);

const BrokerageDashboard = () => {
  const { _t } = useTranslator();
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [, setStartDate] = useState(currentDate);
  const [, setEndDate] = useState(currentDate);

  const handleApplyRange = ({ startDate: start, endDate: end }) => {
    setStartDate(start);
    setEndDate(end);
    setDate(start);
  };

  // Top 4 stat cards
  const topStats = [
    { label: _t("total_paid", "Total Paid"), value: 0, unit: " kg" },
    { label: _t("total_received", "Total Received"), value: 0, unit: " kg" },
    { label: _t("total_sorted", "Total Sorted"), value: 0, unit: " kg" },
    { label: _t("rejected", "Rejected"), value: 0, unit: " kg" },
  ];

  // Processing items
  const processingItems = [
    {
      title: _t("prime", "Prime"),
      total: 0,
      accentColor: "#3b82f6",
      bg: "rgb(230, 245, 233)",
      details: [
        { label: _t("cheeks", "Cheeks"), value: 0 },
        { label: _t("strips", "Strips"), value: 0 },
      ],
    },
    {
      title: _t("second", "Second"),
      total: 0,
      accentColor: "#3b82f6",
      bg: "rgb(225, 240, 255)",
      details: [
        { label: _t("cheeks", "Cheeks"), value: 0 },
        { label: _t("strips", "Strips"), value: 0 },
      ],
    },
    {
      title: _t("pieces", "Pieces"),
      total: 0,
      accentColor: "#ee7225",
      bg: "rgb(252, 232, 234)",
      details: null,
    },
    {
      title: _t("pasting", "Pasting"),
      total: 0,
      accentColor: "#0ea5e9",
      bg: "#e0f2fe",
      details: null,
    },
  ];

  // Sorting box breakdown — grouped by category, matching processing overview style
  const sortingGroups = [
    {
      category: _t("prime", "Prime"),
      total: 0,
      totalLabel: _t("total_prime_boxes", "Total Prime Boxes"),
      bg: "rgb(230, 245, 233)",
      items: [
        { label: _t("cheek_10lb", "Cheek (10 lb)"), value: 0 },
        { label: _t("cheek_20lb", "Cheek (20 lb)"), value: 0 },
        { label: _t("strip_20lb", "Strip (20 lb)"), value: 0 },
      ],
    },
    {
      category: _t("second", "Second"),
      total: 0,
      totalLabel: _t("total_second_boxes", "Total Second Boxes"),
      bg: "rgb(225, 240, 255)",
      items: [
        // { label: _t("cheek_10lb", "Cheek (10 lb)"), value: 0 },
        { label: _t("cheek_20lb", "Cheek (20 lb)"), value: 0 },
        { label: _t("strip_20lb", "Strip (20 lb)"), value: 0 },
      ],
    },
    {
      category: _t("pieces", "Pieces"),
      total: null,
      bg: "rgb(252, 232, 234)",
      items: [
        { label: _t("pieces_20lb", "Pieces (20 lb)"), value: 0 },
      ],
    },
    {
      category: _t("pasting", "Pasting"),
      total: null,
      bg: "#e0f2fe",
      items: [
        { label: _t("pasting_boxes", "Pasting boxes"), value: 0 },
      ],
    },
  ];

  // Day/Night separation
  const separationItems = [
    {
      title: _t("day_separation", "Day Separation"),
      items: [
        { label: _t("strips_kg", "Strips KG"), value: 0, unit: " kg" },
        { label: _t("cheeks_kg", "Cheeks KG"), value: 0, unit: " kg" },
        { label: _t("people_separating", "People separating"), value: 0, unit: "" },
      ],
    },
    {
      title: _t("shift2_separation", "Shift 2 Separating"),
      items: [
        { label: _t("strips_kg", "Strips KG"), value: 0, unit: " kg" },
        { label: _t("cheeks_kg", "Cheeks KG"), value: 0, unit: " kg" },
        { label: _t("people_separating", "People separating"), value: 0, unit: "" },
      ],
    },
  ];

  return (
    <div>
      {/* ── Toolbar ── */}
      <div className="receiving_dashboard_toolbar align-items-center">
        <h5 className="receiving_dashboard_toolbar_title m-0">
          {_t("brokerage_overview", "Brokerage Overview")}
        </h5>
        <div className="receiving_filter_group">
          <DateRangeFilter setDate={setDate} onApplyRange={handleApplyRange} triggerId="brokerage-date-range-filter" />
        </div>
      </div>

      <div>
        {/* ── Single Section: Top Stats + Processing + Sorting ── */}
        <DashboardSection title={_t("brokerage_overview_section", "Overview")}>

          {/* Top 4 Stat Cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
            {topStats.map((stat, i) => (
              <div key={i} className="db-metric-card">
                <div className="db-metric-label">{stat.label}</div>
                <div className="db-metric-value">
                  {stat.value}<span className="db-metric-unit">{stat.unit}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Processing Overview + Sorting Box Breakdown */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>

            {/* Processing Overview */}
            <BrokerageCard title={_t("processing_overview", "Processing overview")} icon={<CycleIconSvg size={15} color="#667085" />}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {processingItems.map((item, i) => (
                  <div key={i} className="pp-brokering-box" style={{ borderRadius: 8, background: item.bg, border: "1px solid transparent", padding: "16px 14px" }}>
                    <div className="pp-brokering-box-label">{item.title}</div>
                    <div className="pp-brokering-box-value" style={{ marginBottom: item.details ? 10 : 0 }}>
                      {item.total}<span style={{ fontSize: 12, fontWeight: 500, color: "#9ca3af" }}> kg</span>
                    </div>
                    {item.details && (
                      <div style={{ borderTop: "1px solid #f0f1f3", paddingTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                        {item.details.map((d, j) => (
                          <div key={j} className="db-avg-row" style={{ padding: "3px 0" }}>
                            <span>{d.label}</span>
                            <span className="db-avg-val">{d.value} kg</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </BrokerageCard>

            {/* Sorting Box Breakdown */}
            <BrokerageCard title={_t("sorting_box_breakdown", "Sorting · box breakdown")} icon={<PackageIconSvg size={15} color="#667085" />}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {sortingGroups.map((group, i) => (
                  <div key={i} className="pp-brokering-box" style={{ borderRadius: 8, background: group.bg, border: "1px solid transparent", padding: "16px 14px" }}>
                    <div className="pp-brokering-box-label">{group.category}</div>
                    {group.total !== null && (
                      <div className="pp-brokering-box-value" style={{ marginBottom: 10 }}>
                        {group.total}<span style={{ fontSize: 12, fontWeight: 500, color: "#9ca3af" }}> boxes</span>
                      </div>
                    )}
                    <div style={{ borderTop: group.total !== null ? "1px solid #f0f1f3" : "none", paddingTop: group.total !== null ? 8 : 0, display: "flex", flexDirection: "column", gap: 4 }}>
                      {group.items.map((item, j) => (
                        <div key={j} className="db-avg-row" style={{ padding: "3px 0" }}>
                          <span>{item.label}</span>
                          <span className="db-avg-val">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </BrokerageCard>
          </div>
        </DashboardSection>

        {/* ── Row 3: Day/Night Separation ── */}
        <DashboardSection title={<span className="d-flex align-items-center gap-2">{_t("day_night_separation", "Day / night separation")}</span>}>
          <div className="db-two-col">
            {separationItems.map((section, i) => (
              <div key={i} className="cmn_card" style={{ overflow: "hidden" }}>
                <div style={{ padding: "12px 16px", background: i === 0 ? "#fff8f5" : "#f0f1fb", borderBottom: "1px solid #f0f1f3" }}>
                  <span style={{ fontFamily: "'Public Sans', serif", fontWeight: 600, fontSize: 15, color: "#1d1f2c", display: "flex", alignItems: "center", gap: 6 }}>
                    <UsersIconSvg size={15} color="#667085" />
                    {section.title}
                  </span>
                </div>
                <div style={{ background: "#fff", padding: "4px 0" }}>
                  {section.items.map((item, j) => (
                    <div key={j} className="db-avg-row" style={{ padding: "10px 16px" }}>
                      <span>{item.label}</span>
                      <span className="db-avg-val">{item.value}{item.unit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DashboardSection>

      </div>
    </div>
  );
};

export default BrokerageDashboard;
